import debounce from "lodash.debounce";
import type { SegmentTrackEvent } from "@beautypie/segment";

import { track } from "tracking/client";
import { getUser } from "lib/user";
import { DataLayerEvent } from "utils/gtm/types";
import { addGTMEvent } from "utils/gtm";
import { Logger } from "lib/logger";
import { getGTMPurchaseType } from "utils/gtm/utils";

type ProductImpression = {
  sku: string;
  timestamp: number;
};

class ProductImpressionManager {
  private static instance: ProductImpressionManager;
  private impressions: Map<string, ProductImpression> = new Map();
  private log = Logger(
    "packages/acquisition/shared/utils/productImpressionManager"
  );

  private debouncedTrack = debounce(() => {
    this.flushImpressions();
  }, 1000);

  static getInstance(): ProductImpressionManager {
    if (!this.instance) {
      this.instance = new ProductImpressionManager();
    }
    return this.instance;
  }

  addImpression(sku: string): void {
    if (!sku) return;

    this.impressions.set(sku, {
      sku,
      timestamp: Date.now()
    });

    this.debouncedTrack();
  }

  private async flushImpressions(): Promise<void> {
    if (this.impressions.size === 0) return;

    const currentImpressions = Array.from(this.impressions.values());
    this.impressions.clear();

    const key = `productListViewed${window.location.pathname}`;
    const store = this.storeFromPathname(window.location.pathname);
    const tracked = JSON.parse(sessionStorage.getItem(key) || "[]");

    const untracked = currentImpressions.filter(
      ({ sku }) => !tracked.includes(sku)
    );

    if (untracked.length === 0) return;

    const skus = untracked.map(({ sku }) => sku);

    // Segment tracking
    await this.trackSegmentImpressions(store, untracked);

    // GTM tracking
    await this.trackGTMImpressions(store, skus);

    // Update session storage
    sessionStorage.setItem(key, JSON.stringify([...tracked, ...skus]));
  }

  private async trackSegmentImpressions(
    store: string,
    impressions: ProductImpression[]
  ): Promise<void> {
    try {
      const user = getUser();
      let segments = window.location.pathname.slice(1).split("/");

      if (segments[0] === store) segments = segments.slice(1);
      const categories = segments[0] === "shop" ? segments.slice(1) : [""];

      const payload = {
        event: "Product List Viewed",
        properties: {
          currency: store === "en" ? "GBP" : "USD",
          product_price_type: user.hasMembership
            ? "Member Price"
            : "Typical Price",
          products: impressions.map(({ sku }) => ({ sku })),
          product_category_1: categories[0] || "",
          product_category_2: categories[1],
          product_category_3: categories[2]
        }
      };

      track(payload as SegmentTrackEvent);
    } catch (error) {
      this.log.error(
        "Failed to send product impressions to Segment",
        JSON.stringify(error)
      );
    }
  }

  private async trackGTMImpressions(
    store: string,
    skus: string[]
  ): Promise<void> {
    try {
      const res = await fetch(
        `/api/variants?skus=${skus.join(",")}&locale=${this.inferLocaleFromStore(store)}`
      );

      if (res.ok) {
        const { variants } = await res.json();
        const purchaseType = getGTMPurchaseType();

        const data = {
          ecommerce: {
            currencyCode: this.currencyCode(store),
            impressions: variants.map((variant, index) => ({
              id: variant?.sku,
              brand: variant?.brandName,
              name: variant?.title,
              variant: variant?.subTitle ?? "",
              price: variant?.pricing.members,
              metric7: variant?.pricing.typical,
              position: index + 1
            }))
          },
          product_price_type: purchaseType
        };

        addGTMEvent({ name: DataLayerEvent.ProductImpression, data });
      }
    } catch (error) {
      this.log.error(
        "Failed to send product impressions to GTM",
        JSON.stringify(error)
      );
    }
  }

  private storeFromPathname(pathname: string): string {
    const [first] = pathname.slice(1).split("/");
    return first === "us" ? "us" : "en";
  }

  private currencyCode(store: string): string {
    switch (store) {
      case "us":
        return "USD";
      case "uk":
      default:
        return "GBP";
    }
  }

  private inferLocaleFromStore(store: string): string {
    switch (store) {
      case "us":
        return "en-US";
      case "uk":
      default:
        return "en-GB";
    }
  }
}

export const productImpressionManager = ProductImpressionManager.getInstance();
